"use client"
import IconChevronLeft from "@/app/(site)/_components/Atoms/Icons/IconChevronLeft"
import IconChevronRight from "@/app/(site)/_components/Atoms/Icons/IconChevronRight"
import { useIsMobile } from "@/app/(site)/_utils/_hooks/useIsMobile"
import { FOCUS_VISIBLE_CLASS } from "@/src/utils/a11y/focusVisible"
import { HIT_TARGET_PSEUDO_CLASS } from "@/src/utils/a11y/hitTarget"
import { clsx } from "@/src/utils/clsx"
import { t } from "@/translations"
import { type ReactNode, useEffect, useRef, useState } from "react"

export default function AnimationSliderWrapper({
    children,
    langCode
}: {
    children: ReactNode
    langCode: string
}) {
    const language = langCode || "en"
    const isMobile = useIsMobile()
    const sliderRef = useRef<HTMLDivElement>(null)
    const [isAtStart, setIsAtStart] = useState(true)
    const [isAtEnd, setIsAtEnd] = useState(false)
    const scrollPercentage = 0.4

    const handleScroll = () => {
        if (sliderRef.current) {
            setIsAtStart(sliderRef.current.scrollLeft === 0)
            setIsAtEnd(
                sliderRef.current.scrollWidth <=
                    sliderRef.current.scrollLeft + sliderRef.current.clientWidth
            )
        }
    }

    const handleScrollLeft = () => {
        if (sliderRef.current) {
            const scrollAmount = sliderRef.current.clientWidth * scrollPercentage
            sliderRef.current.scrollBy({ left: -scrollAmount, behavior: "smooth" })
        }
    }

    const handleScrollRight = () => {
        if (sliderRef.current) {
            const scrollAmount = sliderRef.current.clientWidth * scrollPercentage
            sliderRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" })
        }
    }

    const handleResize = () => {
        handleScroll()
    }

    useEffect(() => {
        if (typeof window === "undefined") return
        handleScroll()
        sliderRef.current?.addEventListener("scroll", handleScroll, { passive: true })
        window.addEventListener("resize", handleResize)
        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            sliderRef.current?.removeEventListener("scroll", handleScroll)
            window.removeEventListener("resize", handleResize)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div className="relative">
            {!isAtStart && !isMobile && (
                <button
                    type="button"
                    onClick={handleScrollLeft}
                    aria-label={t(language, "common.previous_item")}
                    className={clsx(
                        "absolute left-0 top-1/2 z-10 ml-8 -translate-y-1/2 transform rounded-sm bg-white p-5 md:ml-16",
                        FOCUS_VISIBLE_CLASS,
                        HIT_TARGET_PSEUDO_CLASS
                    )}
                >
                    <span aria-hidden="true">
                        <IconChevronLeft />
                    </span>
                </button>
            )}
            <div
                ref={sliderRef}
                tabIndex={-1}
                className="scrollbar-visible flex gap-16 overflow-x-scroll border-b-gray-500 pb-28 scrollbar md:gap-24 md:pb-48"
            >
                {children}
            </div>
            {!isAtEnd && !isMobile && (
                <button
                    type="button"
                    onClick={handleScrollRight}
                    aria-label={t(language, "common.next_item")}
                    className={clsx(
                        "absolute right-0 top-1/2 mr-8 -translate-y-1/2 transform rounded-sm bg-white p-5 md:mr-16",
                        FOCUS_VISIBLE_CLASS,
                        HIT_TARGET_PSEUDO_CLASS
                    )}
                >
                    <span aria-hidden="true">
                        <IconChevronRight />
                    </span>
                </button>
            )}
        </div>
    )
}
